import * as React from "react";
import {
  makeStyles,
  Text,
  Button,
  Dropdown,
  Option,
  Switch,
  Checkbox,
  Input,
  Field,
  TabList,
  Tab,
} from "@fluentui/react-components";
import { Dismiss24Regular, Delete16Regular, Add16Regular } from "@fluentui/react-icons";
import {
  PERSONAS,
  INTENTS,
  SUMMARY_STYLES,
  REPLY_TONES,
  LANGUAGES,
} from "../../shared/constants";
import WorkflowBuilder from "./WorkflowBuilder";

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "var(--overlay, rgba(0,0,0,0.3))",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
  },
  drawer: {
    backgroundColor: "var(--bg-secondary, #F8F9FA)",
    borderRadius: "16px 16px 0 0",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: "0 16px 24px 16px",
    boxShadow: "var(--shadow-lg, 0 8px 24px rgba(11,17,26,0.12))",
  },
  handle: { display: "flex", justifyContent: "center", padding: "12px 0 8px 0" },
  handleBar: {
    width: "40px",
    height: "4px",
    borderRadius: "9999px",
    backgroundColor: "var(--border-secondary, #C8CBD0)",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  tabs: { marginBottom: "12px" },
  section: { marginBottom: "16px" },
  intentGrid: { display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "4px" },
  divider: { border: "none", borderTop: "1px solid var(--border-primary, #E0E2E6)", margin: "16px 0" },
  customList: { display: "flex", flexDirection: "column", gap: "8px" },
  customRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 10px",
    borderRadius: "8px",
    backgroundColor: "var(--bg-elevated, #FFFFFF)",
    border: "1px solid var(--border-primary, #E0E2E6)",
  },
  customName: { flex: 1, fontSize: "13px", fontWeight: "500" },
  customMeta: { fontSize: "11px", color: "var(--text-tertiary, #6E747D)" },
  empty: {
    textAlign: "center",
    padding: "16px",
    fontSize: "12px",
    color: "var(--text-tertiary, #6E747D)",
  },
  newBtn: {
    backgroundColor: "#FFB500",
    color: "#0B111A",
    fontWeight: "600",
    border: "none",
    ":hover": { backgroundColor: "#E6A300" },
  },
});

const SettingsDrawer = ({ open, onClose, config, onConfigChange }) => {
  const styles = useStyles();
  const [tab, setTab] = React.useState("general");
  const [editing, setEditing] = React.useState(null); // null | { mode: 'new' | 'edit', workflow }

  if (!open) return null;

  const persona = config.persona;
  const customForPersona = (config.customWorkflows || {})[persona] || [];

  const toggleIntent = (intentKey) => {
    const current = config.enabledIntents || [];
    const next = current.includes(intentKey)
      ? current.filter((k) => k !== intentKey)
      : [...current, intentKey];
    onConfigChange({ enabledIntents: next });
  };

  const saveCustomWorkflow = (wf) => {
    const all = { ...(config.customWorkflows || {}) };
    const list = [...(all[persona] || [])];
    if (editing?.mode === "edit") {
      const idx = list.findIndex((w) => w.id === editing.workflow.id);
      if (idx >= 0) list[idx] = { ...wf, id: editing.workflow.id };
      else list.push(wf);
    } else {
      list.push(wf);
    }
    all[persona] = list;
    onConfigChange({ customWorkflows: all });
    setEditing(null);
  };

  const deleteCustomWorkflow = (id) => {
    const all = { ...(config.customWorkflows || {}) };
    all[persona] = (all[persona] || []).filter((w) => w.id !== id);
    onConfigChange({ customWorkflows: all });
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.handle}>
          <div className={styles.handleBar} />
        </div>

        <div className={styles.headerRow}>
          <Text size={500} weight="semibold">Settings</Text>
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={onClose}
            aria-label="Close settings"
            size="small"
          />
        </div>

        <TabList
          className={styles.tabs}
          selectedValue={tab}
          onTabSelect={(_, d) => setTab(d.value)}
          size="small"
        >
          <Tab value="general">General</Tab>
          <Tab value="workflows">Workflows</Tab>
          <Tab value="advanced">Advanced</Tab>
        </TabList>

        {tab === "general" && (
          <>
            <div className={styles.section}>
              <Field label="Persona">
                <Dropdown
                  value={PERSONAS.find((p) => p.key === config.persona)?.label || ""}
                  onOptionSelect={(_, data) => onConfigChange({ persona: data.optionValue })}
                >
                  {PERSONAS.map((p) => (
                    <Option key={p.key} value={p.key}>{p.label}</Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
            <div className={styles.section}>
              <Switch
                label="Auto-trigger on email open"
                checked={config.autoTrigger}
                onChange={(_, data) => onConfigChange({ autoTrigger: data.checked })}
              />
            </div>
            <div className={styles.section}>
              <Text size={300} weight="semibold" style={{ display: "block", marginBottom: "4px" }}>
                Default Enabled Intents
              </Text>
              <div className={styles.intentGrid}>
                {INTENTS.map((intent) => (
                  <Checkbox
                    key={intent.key}
                    label={intent.label}
                    checked={(config.enabledIntents || []).includes(intent.key)}
                    onChange={() => toggleIntent(intent.key)}
                  />
                ))}
              </div>
            </div>
            <div className={styles.section}>
              <Field label="Summary Style">
                <Dropdown
                  value={SUMMARY_STYLES.find((s) => s.key === config.summaryStyle)?.label || ""}
                  onOptionSelect={(_, data) => onConfigChange({ summaryStyle: data.optionValue })}
                >
                  {SUMMARY_STYLES.map((s) => (
                    <Option key={s.key} value={s.key}>{s.label}</Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
            <div className={styles.section}>
              <Field label="Reply Tone">
                <Dropdown
                  value={REPLY_TONES.find((t) => t.key === config.replyTone)?.label || ""}
                  onOptionSelect={(_, data) => onConfigChange({ replyTone: data.optionValue })}
                >
                  {REPLY_TONES.map((t) => (
                    <Option key={t.key} value={t.key}>{t.label}</Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
            <div className={styles.section}>
              <Field label="Target Language (default for translations)">
                <Dropdown
                  value={LANGUAGES.find((l) => l.key === config.targetLanguage)?.label || ""}
                  onOptionSelect={(_, data) => onConfigChange({ targetLanguage: data.optionValue })}
                >
                  {LANGUAGES.map((l) => (
                    <Option key={l.key} value={l.key}>{l.label}</Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
          </>
        )}

        {tab === "workflows" && (
          <>
            {!editing && (
              <>
                <Text style={{ fontSize: "12px", color: "var(--text-tertiary, #6E747D)", display: "block", marginBottom: "8px" }}>
                  Custom workflows for persona: <strong>{PERSONAS.find((p) => p.key === persona)?.label}</strong>
                </Text>
                <div className={styles.customList}>
                  {customForPersona.length === 0 && (
                    <div className={styles.empty}>No custom workflows yet for this persona.</div>
                  )}
                  {customForPersona.map((wf) => (
                    <div key={wf.id} className={styles.customRow}>
                      <div style={{ flex: 1 }}>
                        <div className={styles.customName}>{wf.name}</div>
                        <div className={styles.customMeta}>
                          {wf.dag.length} step{wf.dag.length > 1 ? "s" : ""} · {wf.output_cards.length} output
                          {wf.output_cards.length > 1 ? "s" : ""}
                        </div>
                      </div>
                      <Button
                        size="small"
                        appearance="subtle"
                        onClick={() => setEditing({ mode: "edit", workflow: wf })}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Delete16Regular />}
                        onClick={() => deleteCustomWorkflow(wf.id)}
                        aria-label="Delete"
                      />
                    </div>
                  ))}
                </div>
                <hr className={styles.divider} />
                <Button
                  appearance="primary"
                  className={styles.newBtn}
                  icon={<Add16Regular />}
                  onClick={() => setEditing({ mode: "new" })}
                >
                  New custom workflow
                </Button>
              </>
            )}
            {editing && (
              <WorkflowBuilder
                initialName={editing.workflow?.name || ""}
                initialNodes={(editing.workflow?.dag || []).map((n, i, arr) => ({
                  id: n.id,
                  wf: n.wf,
                  parallelWithPrev: i > 0 && n.parallel_group && arr[i - 1].parallel_group === n.parallel_group,
                }))}
                onSave={saveCustomWorkflow}
                onCancel={() => setEditing(null)}
              />
            )}
          </>
        )}

        {tab === "advanced" && (
          <div className={styles.section}>
            <Field label="n8n Webhook URL">
              <Input
                value={config.n8nUrl || ""}
                onChange={(_, data) => onConfigChange({ n8nUrl: data.value })}
                placeholder="http://localhost:5678/webhook/dispatch"
                type="url"
              />
            </Field>
            <Text size={200} style={{ display: "block", marginTop: "8px", color: "var(--text-tertiary, #6E747D)" }}>
              Default points to local docker stack. Update for hosted n8n.
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsDrawer;
